import { createClientLogger } from '@/client/logging/client-logger';
import { browserIncidentLaunchDarklySender } from '@/client/observability/browser-incident-launchdarkly-sender';
import { browserIncidentRelaySender } from '@/client/observability/browser-incident-relay-sender';
import {
    resolveBrowserIncidentDispatchSinks,
    resolveIncidentDispatchClassification,
    toDispatchedIncidentMetadataRecord,
    type BrowserDispatchedIncident,
    type BrowserIncidentDispatchSink,
    type IncidentDispatchAttempt,
} from '@/shared/observability/incident-dispatch';

const browserIncidentDispatchLogger = createClientLogger({ scope: 'browser-incident-dispatcher' });
const BROWSER_INCIDENT_SENDERS = [browserIncidentLaunchDarklySender, browserIncidentRelaySender];

export type BrowserIncidentDispatchResult = {
    classification: BrowserDispatchedIncident['classification'];
    attempts: IncidentDispatchAttempt<BrowserIncidentDispatchSink>[];
};

const toErrorMessage = (error: unknown): string => {
    return error instanceof Error ? error.message : String(error);
};

export const dispatchBrowserIncident = async (
    input: BrowserDispatchedIncident,
): Promise<BrowserIncidentDispatchResult> => {
    const classification = resolveIncidentDispatchClassification(input);
    const resolvedIncident: BrowserDispatchedIncident = {
        ...input,
        classification,
        disposition: input.disposition ?? 'terminal',
        metadata: toDispatchedIncidentMetadataRecord(input.metadata),
        keepalive: input.keepalive ?? true,
    };
    const dispatchableSinks = new Set(
        resolveBrowserIncidentDispatchSinks({
            classification,
            disposition: resolvedIncident.disposition,
        }),
    );

    const attempts = await Promise.all(
        BROWSER_INCIDENT_SENDERS.filter((sender) => dispatchableSinks.has(sender.sink)).map(async (sender) => {
            try {
                const result = await sender.send(resolvedIncident);
                return {
                    sink: sender.sink,
                    delivered: result.delivered,
                    reason: result.reason,
                    duplicateCount: result.duplicateCount ?? 0,
                } satisfies IncidentDispatchAttempt<BrowserIncidentDispatchSink>;
            } catch (error) {
                return {
                    sink: sender.sink,
                    delivered: false,
                    reason: 'failed',
                    duplicateCount: 0,
                    error: toErrorMessage(error),
                } satisfies IncidentDispatchAttempt<BrowserIncidentDispatchSink>;
            }
        }),
    );

    for (const attempt of attempts) {
        if (attempt.reason !== 'failed') {
            continue;
        }

        browserIncidentDispatchLogger.warn(
            '[client.runtime-observability.incident-dispatch] Failed to deliver browser incident to sink',
            {
                sink: attempt.sink,
                classification,
                fingerprint: resolvedIncident.fingerprint,
                incidentDispatchError: attempt.error,
            },
        );
    }

    return {
        classification,
        attempts,
    };
};
